import React, { useState, useEffect } from "react";
import {
  ChevronDown,
  ChevronUp,
  ArrowUp,
  ArrowDown,
  Trash2,
} from "lucide-react";
import SectionFieldEditor from "./SectionFieldEditor";
import { SECTION_TYPE_MAP } from "../../lib/cms/sectionTypes";

export interface SectionDraft {
  _id: string;
  type: string;
  [key: string]: unknown;
}

interface SectionCardProps {
  section: SectionDraft;
  index: number;
  total: number;
  onUpdate: (id: string, data: Record<string, unknown>) => void;
  onRemove: (id: string) => void;
  onMoveUp: (id: string) => void;
  onMoveDown: (id: string) => void;
}

function getPreview(section: SectionDraft): string {
  switch (section.type) {
    case "hero":
      return (section.heading as string) || "(sem título)";
    case "richtext": {
      const html = (section.html as string) || "";
      const stripped = html.replace(/<[^>]*>/g, "");
      return stripped.length > 60
        ? stripped.substring(0, 60) + "..."
        : stripped || "(vazio)";
    }
    case "faq_list": {
      const items = Array.isArray(section.items) ? section.items : [];
      return `${items.length} ${items.length === 1 ? "pergunta" : "perguntas"}`;
    }
    case "sections_list": {
      const items = Array.isArray(section.items) ? section.items : [];
      return `${items.length} ${items.length === 1 ? "secção" : "secções"}`;
    }
    case "stats_grid": {
      const items = Array.isArray(section.items) ? section.items : [];
      return `${items.length} ${items.length === 1 ? "estatística" : "estatísticas"}`;
    }
    case "contact_info":
      return (
        (section.email as string) ||
        (section.phone as string) ||
        "(sem contactos)"
      );
    case "stores_list": {
      const items = Array.isArray(section.items) ? section.items : [];
      return `${items.length} ${items.length === 1 ? "loja" : "lojas"}`;
    }
    case "services_grid": {
      const items = Array.isArray(section.items) ? section.items : [];
      return (
        (section.heading as string) ||
        `${items.length} ${items.length === 1 ? "serviço" : "serviços"}`
      );
    }
    case "payment_methods": {
      const items = Array.isArray(section.items) ? section.items : [];
      return `${items.length} ${items.length === 1 ? "método" : "métodos"}`;
    }
    case "bullet_list": {
      const items = Array.isArray(section.items) ? section.items : [];
      return `${items.length} ${items.length === 1 ? "item" : "itens"}`;
    }
    case "product_grid": {
      const brand = (section.brand as string) || "";
      const heading = (section.heading as string) || "";
      return heading || brand
        ? `${heading || "Grelha de produtos"}${brand ? ` · ${brand}` : ""}`
        : "Grelha de produtos (catálogo)";
    }
    case "hero_banner":
      return (
        (section.heading as string) ||
        (section.badge as string) ||
        "Banner hero"
      );
    case "callout":
      return (section.heading as string) || "Destaque";
    case "feature_cards": {
      const items = Array.isArray(section.items) ? section.items : [];
      return (
        (section.heading as string) ||
        `${items.length} ${items.length === 1 ? "cartão" : "cartões"}`
      );
    }
    default:
      console.warn(
        `[SectionCard] Unknown section type in preview: ${section.type}`,
      );
      return JSON.stringify(section).substring(0, 60);
  }
}

export default function SectionCard({
  section,
  index,
  total,
  onUpdate,
  onRemove,
  onMoveUp,
  onMoveDown,
}: SectionCardProps) {
  const [expanded, setExpanded] = useState(total === 1);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!confirmDelete) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setConfirmDelete(false);
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [confirmDelete]);

  const typeInfo = SECTION_TYPE_MAP.get(section.type);
  const typeLabel = typeInfo?.label || section.type;
  const typeColor = typeInfo?.color || "bg-gray-100 text-gray-700";
  const preview = getPreview(section);

  return (
    <div
      className="border border-gray-200 rounded-lg bg-white shadow-sm"
      onKeyDown={(e) => {
        // Escape here cancels the inline delete confirm; it must not bubble to
        // the surrounding ModalScreen and close the whole editor.
        if (confirmDelete && e.key === "Escape") {
          e.stopPropagation();
          setConfirmDelete(false);
        }
      }}
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3">
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          aria-expanded={expanded}
          aria-controls={`section-body-${section._id}`}
          className="flex items-center gap-2 flex-1 text-left min-w-0"
        >
          <span className="shrink-0">
            {expanded ? (
              <ChevronUp size={16} className="text-gray-400" />
            ) : (
              <ChevronDown size={16} className="text-gray-400" />
            )}
          </span>
          <span
            className={`px-2 py-0.5 rounded text-xs font-medium shrink-0 ${typeColor}`}
          >
            {typeLabel}
          </span>
          <span className="text-sm text-gray-600 truncate min-w-0">
            {expanded ? typeLabel : preview}
          </span>
        </button>

        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={() => onMoveUp(section._id)}
            disabled={index === 0}
            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label="Mover secção para cima"
            title="Mover para cima"
          >
            <ArrowUp size={16} />
          </button>
          <button
            type="button"
            onClick={() => onMoveDown(section._id)}
            disabled={index === total - 1}
            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label="Mover secção para baixo"
            title="Mover para baixo"
          >
            <ArrowDown size={16} />
          </button>

          {confirmDelete ? (
            <span className="flex items-center gap-1.5 text-xs bg-red-50 px-2 py-1 rounded-md">
              <span className="text-red-600">Remover?</span>
              <button
                type="button"
                onClick={() => {
                  onRemove(section._id);
                  setConfirmDelete(false);
                }}
                className="text-red-700 font-medium hover:underline"
              >
                Sim
              </button>
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                className="text-gray-500 hover:underline"
              >
                Não
              </button>
            </span>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              className="p-1 text-red-400 hover:text-red-600"
              aria-label="Remover secção"
              title="Remover secção"
            >
              <Trash2 size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      {expanded && (
        <div
          id={`section-body-${section._id}`}
          className="px-4 pb-4 border-t border-gray-100 pt-3"
        >
          <SectionFieldEditor
            type={section.type}
            data={section}
            onChange={(data) => onUpdate(section._id, data)}
          />
        </div>
      )}
    </div>
  );
}
